import { useEffect, useRef } from 'react'
import './splash.css'

interface SplashScreenProps {
  onComplete: () => void
}

export function SplashScreen({ onComplete }: SplashScreenProps) {
  const letterRefs = useRef<(SVGPathElement | null)[]>([])

  useEffect(() => {
    const letterCount = 11
    const allTimers: ReturnType<typeof setTimeout>[] = []

    // Pour chaque lettre : flicker indépendant avec accélération progressive
    // puis accrochage en blanc à un instant aléatoire entre 1200ms et 2000ms
    for (let i = 0; i < letterCount; i++) {
      const el = letterRefs.current[i]
      if (!el) continue

      // Moment où cette lettre se fixe définitivement en blanc
      const fixAt = 600 + Math.random() * 400

      let isWhite = false
      let stopped = false

      // Boucle récursive de flicker : chaque tick réduit l'intervalle
      // Formule : interval = max(20, initialInterval * exp(-k * elapsed))
      const initialInterval = 125 + Math.random() * 50 // démarrage ~125-175ms
      const decayRate = 2.8 // vitesse d'accélération
      const startTime = performance.now()

      const tick = () => {
        if (stopped) return

        const elapsed = (performance.now() - startTime) / 1000
        const nextInterval = Math.max(20, initialInterval * Math.exp(-decayRate * elapsed))

        // Toggle fill
        isWhite = !isWhite
        el.style.fill = isWhite ? '#FFFFFF' : '#222222'

        const t = setTimeout(tick, nextInterval)
        allTimers.push(t)
      }

      // Premier tick après un délai aléatoire initial (étalement des démarrages)
      const initDelay = Math.random() * 100
      const initTimer = setTimeout(tick, initDelay)
      allTimers.push(initTimer)

      // Timer de fixation : arrête le flicker et force le blanc
      const fixTimer = setTimeout(() => {
        stopped = true
        el.style.fill = '#FFFFFF'
      }, fixAt)
      allTimers.push(fixTimer)
    }

    // Fade-out de l'overlay après 1.5s (fade CSS démarre à 1.25s, dure 0.25s)
    const endTimer = setTimeout(() => onComplete(), 1500)
    allTimers.push(endTimer)

    return () => {
      allTimers.forEach(t => clearTimeout(t))
    }
  }, [onComplete])

  return (
    <div className="splash-overlay" aria-hidden="true">
      <div className="splash-logo-wrap">
        <svg
          className="splash-svg"
          viewBox="0 0 595.28 208.87"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="Motherboard satellite"
        >
          {/* M */}
          <path ref={el => { letterRefs.current[0] = el }} className="splash-letter" d="M110.92,157.02h-18.6l1.17-88.09-.2-23.49h-.39l-1.37,23.49-7.05,88.09h-17.42l-7.05-88.09-1.57-23.49h-.39l-.2,23.49,1.17,88.09h-18.6V19.99h31.91l2.35,78.11.98,21.73h.39l.78-21.73,2.15-78.11h31.91v137.03Z" />
          {/* O */}
          <path ref={el => { letterRefs.current[1] = el }} className="splash-letter" d="M136.76,18.82c-12.33,0-21.73,8.03-21.73,21.53v96.31c0,13.7,9,21.53,21.53,21.53s21.53-7.83,21.53-21.53V40.35c0-13.7-9-21.53-21.34-21.53ZM138.52,137.64c0,1.96-.59,2.94-1.96,2.94s-1.96-.98-1.96-2.94V39.37c0-1.96.59-2.94,1.96-2.94s1.96.98,1.96,2.94v98.27Z" />
          {/* T */}
          <path ref={el => { letterRefs.current[2] = el }} className="splash-letter" d="M200.18,37.61h-9.79v119.41h-19.58V37.61h-9.79v-17.62h39.15v17.62Z" />
          {/* H */}
          <path ref={el => { letterRefs.current[3] = el }} className="splash-letter" d="M246.18,157.02h-19.58v-59.51h-3.92v59.51h-19.58V19.99h19.58v59.9h3.92V19.99h19.58v137.03Z" />
          {/* E */}
          <path ref={el => { letterRefs.current[4] = el }} className="splash-letter" d="M277.5,37.61h-7.83v40.33h6.85v17.62h-6.85v43.85h8.81v17.62h-28.38V19.99h27.41v17.62Z" />
          {/* R */}
          <path ref={el => { letterRefs.current[5] = el }} className="splash-letter" d="M302.95,96.34h-1.96v60.68h-19.58V19.99h21.73c14.29,0,21.34,5.87,21.34,19.58v27.8c0,11.75-.78,17.42-8.22,19.97v.39c7.44,2.35,8.22,8.22,8.22,19.97v31.32c0,8.81.2,11.55,2.35,17.62v.39h-20.16c-1.37-5.29-1.76-8.81-1.76-17.81v-39.93c0-1.96-.59-2.94-1.96-2.94ZM302.95,37.61h-1.96v41.11h1.96c1.37,0,1.96-.98,1.96-2.94v-35.24c0-1.96-.59-2.94-1.96-2.94Z" />
          {/* B */}
          <path ref={el => { letterRefs.current[6] = el }} className="splash-letter" d="M371.46,63.45v-23.88c0-13.7-7.05-19.58-21.34-19.58h-21.73v137.03h21.53c14.49,0,21.53-5.87,21.53-19.58v-33.67c0-11.75-.78-17.62-8.22-19.97v-.39c7.44-2.54,8.22-8.22,8.22-19.97ZM351.89,136.47c0,1.96-.59,2.94-1.96,2.94h-1.96v-46.98h1.96c1.37,0,1.96.98,1.96,2.94v41.11ZM351.89,71.87c0,1.96-.59,2.94-1.96,2.94h-1.96v-37.19h1.96c1.37,0,1.96.98,1.96,2.94v31.32Z" />
          {/* O */}
          <path ref={el => { letterRefs.current[7] = el }} className="splash-letter" d="M396.72,18.82c-12.33,0-21.73,8.03-21.73,21.53v96.31c0,13.7,9,21.53,21.53,21.53s21.53-7.83,21.53-21.53V40.35c0-13.7-9-21.53-21.34-21.53ZM398.48,137.64c0,1.96-.59,2.94-1.96,2.94s-1.96-.98-1.96-2.94V39.37c0-1.96.59-2.94,1.96-2.94s1.96.98,1.96,2.94v98.27Z" />
          {/* A */}
          <path ref={el => { letterRefs.current[8] = el }} className="splash-letter" d="M458.18,19.99h-27.41l-10.77,137.03h19.58l1.17-32.69h5.48l1.17,32.69h21.53l-10.77-137.03ZM441.35,106.71l1.37-41.5.59-23.69h.39l.59,23.69,1.37,41.5h-4.31Z" />
          {/* R */}
          <path ref={el => { letterRefs.current[9] = el }} className="splash-letter" d="M513.97,139.01v-31.32c0-11.75-.78-17.62-8.22-19.97v-.39c7.44-2.54,8.22-8.22,8.22-19.97v-27.8c0-13.7-7.05-19.58-21.34-19.58h-21.73v137.03h19.58v-60.68h1.96c1.37,0,1.96.98,1.96,2.94v39.93c0,9,.39,12.53,1.76,17.81h20.16v-.39c-2.15-6.07-2.35-8.81-2.35-17.62ZM494.4,75.78c0,1.96-.59,2.94-1.96,2.94h-1.96v-41.11h1.96c1.37,0,1.96.98,1.96,2.94v35.24Z" />
          {/* D */}
          <path ref={el => { letterRefs.current[10] = el }} className="splash-letter" d="M539.62,19.99h-21.73v137.03h21.53c14.49,0,21.53-5.87,21.53-19.58V39.57c0-13.7-7.05-19.58-21.34-19.58ZM541.38,136.47c0,1.96-.59,2.94-1.96,2.94h-1.96V37.61h1.96c1.37,0,1.96.98,1.96,2.94v95.92Z" />

          {/* satellite — 9 glyphes */}
          <path className="splash-satellite" d="M46.11,182.32h-6.49v-8.04h6.49v4.4h14.05v2.24h6.49v9.89h-6.49v4.34h-14.05v-6.49h14.05v-3.48h-14.05v-2.84ZM60.16,174.28h-14.05v-6.49h14.05v6.49Z" />
          <path className="splash-satellite" d="M73.43,195.13h-4.71v-5.2l22.14-22.32h4.86v5.11l-6.46,6.49h6.48v15.91h-6.49v-15.9l-15.83,15.9Z" />
          <path className="splash-satellite" d="M109.23,174.08h-10.22v-6.49h10.22v6.49ZM115.73,195.13h-6.49v-20.85h6.49v20.85ZM126.01,174.08h-10.22v-6.49h10.22v6.49Z" />
          <path className="splash-satellite" d="M155.48,174.25h-27.29v-6.49h27.29v6.49ZM139.56,195.11l-11.37-11.64v-5.2h5.2l5.71,6.15v-6.43h16.38v6.49h-16.33l3.84,4.14h12.49v6.49h-15.92Z" />
          <path className="splash-satellite" d="M166.5,167.61v21.04h-6.49v-21.04h6.49ZM166.5,188.65h20.54v6.49h-20.54v-6.49Z" />
          <path className="splash-satellite" d="M196.54,167.61v21.04h-6.49v-21.04h6.49ZM196.54,188.65h20.54v6.49h-20.54v-6.49Z" />
          <path className="splash-satellite" d="M229.79,174.1h-10.32v-6.49h10.32v6.49ZM236.36,188.62h10.4v6.49h-27.29v-6.49h10.4v-14.52h6.49v14.52ZM236.44,167.61h10.32v6.49h-10.32v-6.49Z" />
          <path className="splash-satellite" d="M259.35,174.08h-10.22v-6.49h10.22v6.49ZM265.85,195.13h-6.49v-20.85h6.49v20.85ZM276.13,174.08h-10.22v-6.49h10.22v6.49Z" />
          <path className="splash-satellite" d="M305.59,174.25h-27.29v-6.49h27.29v6.49ZM289.68,195.11l-11.37-11.64v-5.2h5.2l5.71,6.15v-6.43h16.38v6.49h-16.33l3.84,4.14h12.49v6.49h-15.92Z" />
        </svg>
      </div>
    </div>
  )
}
